import { AssignmentDto, AssignmentSubtableDataDto, AssignmentType } from '../generated/backend';
import { AssignmentHelper } from './assignment-helper';

/**
 * An {@link AssignmentDto} enriched with values derived on the client
 * (effective/remaining volume & weight) plus optionally loaded subtable data.
 *
 * Shared by the assignment list grid and the (search / prime-logistic stations)
 * data grid so the shape is defined in exactly one place.
 */
export type AssignmentExtendedDto = AssignmentDto & {
  subtableData?: AssignmentSubtableDataDto;
  TransportLabel?: string;
  EffectiveVolume?: number;
  EffectiveWeight?: number;
  RemainingDispoVol?: number;
  RemainingDispoWeight?: number;
  ShowEventTime?: boolean;
  /**
   * Visual-only effective event date for a Prime Logistic main assignment: the event
   * date advances to the next not-fully-assigned station once earlier stations are
   * fully assigned. Undefined for non-PL assignments (fall back to EventDate).
   * Does NOT change the underlying EventDate data.
   */
  EffectiveEventDate?: string;
};

export enum Logistikkennzeichen {
  AbUhrzeit = 'A',
  BisUhrzeit = 'B',
  FixeUhrzeit = 'U',
  // and some more
}
/** Logistikkennzeichen values for which the event time shall be displayed. */
const EVENT_TIME_INDICATORS: readonly string[] = [
  Logistikkennzeichen.AbUhrzeit,
  Logistikkennzeichen.BisUhrzeit,
  Logistikkennzeichen.FixeUhrzeit,
];

/**
 * Enrich a raw {@link AssignmentDto} with the client-derived effective
 * volume & weight fields.
 *
 * NOTE: `RemainingDispoVol` / `RemainingDispoWeight` are intentionally NOT set
 * here. The two grids currently derive them with different formulas
 * (assignment-list stores the disposed amount, the data grid stores
 * total − disposed). Until that discrepancy is reconciled, each component keeps
 * its own remaining-dispo calculation and only passes it via
 * {@link RemainingDispoOverrides} if it wants those fields populated.
 */
export function toAssignmentExtendedDto(
  assignment: AssignmentDto,
  remaining?: RemainingDispoOverrides,
): AssignmentExtendedDto {
  const row: AssignmentExtendedDto = { ...assignment };
  row.EffectiveVolume = AssignmentHelper.effectiveVolume(assignment);
  row.EffectiveWeight = AssignmentHelper.effectiveWeight(assignment);
  if (remaining) {
    row.RemainingDispoVol = remaining.RemainingDispoVol;
    row.RemainingDispoWeight = remaining.RemainingDispoWeight;
  }
  row.ShowEventTime = showEventTime(assignment);
  return row;
}

/** The subset of fields {@link showEventTime} needs, shared by {@link AssignmentDto} and {@link ProjectLoadunitDto}. */
export interface EventTimeSource {
  Type: AssignmentType;
  T1?: string;
  T2?: string;
}

export function showEventTime(assignment: EventTimeSource): boolean {
  return (
    assignment.Type === AssignmentType.AssignmentDelivery ||
    assignment.Type === AssignmentType.AssignmentPickup ||
    (assignment.T1 !== undefined && EVENT_TIME_INDICATORS.includes(assignment.T1)) ||
    (assignment.T2 !== undefined && EVENT_TIME_INDICATORS.includes(assignment.T2))
  );
}

/** Explicit remaining-dispo values a caller can attach to the extended DTO. */
export interface RemainingDispoOverrides {
  RemainingDispoVol: number;
  RemainingDispoWeight: number;
}

// ---------------------------------------------------------------------------
// Info-badge predicates (shared by both grids)
// ---------------------------------------------------------------------------

/** Assignment is flagged as "far away". */
export function isFarAway(data: AssignmentDto & { TransportLabel?: string }): boolean {
  return data.TransportLabel === 'FarAway';
}

/** Delivery with a Mission-Impossible order in warning state. */
export function isMiWarning(data: AssignmentDto): boolean {
  return data.Type === AssignmentType.AssignmentDelivery && data.MIAuftrag === 1;
}

/** Delivery with a Mission-Impossible order in ok state. */
export function isMiOk(data: AssignmentDto): boolean {
  return data.Type === AssignmentType.AssignmentDelivery && data.MIAuftrag === 2;
}

/** Pickup that has one or more Mission-Impossible problem solutions. */
export function isMiProbSolutions(data: AssignmentDto): boolean {
  return (
    data.Type === AssignmentType.AssignmentPickup &&
    data.NumMIProbSolutions !== undefined &&
    data.NumMIProbSolutions > 0
  );
}

/** Main assignment of a Prime Logistic group (not a station). */
export function isMainPrimeLogisticAssignment(data: AssignmentDto): boolean {
  return data.IsPrimeLogistic === true && data.IsPrimeLogisticStation === false;
}

/** A Prime Logistic station that is fully assigned (its planned amount reached the main assignment's available amount). */
export function isPlannedStation(station: AssignmentDto): boolean {
  return (
    station.IsPrimeLogisticStation === true &&
    station.StationFullyAssigned === true
  );
}

/**
 * Visual-only effective event date for a Prime Logistic main assignment.
 *
 * Rule: while the earliest stations (by event date) are fully assigned, the main's
 * shown action date advances to the first NOT fully-assigned (open) station's event
 * date. If ALL stations are fully assigned, the main's ORIGINAL event date is shown.
 *
 * Returns undefined when it cannot be determined (no stations / no dates) so callers
 * fall back to the original EventDate. Never mutates any data.
 */
export function effectivePrimeLogisticEventDate(
  main: AssignmentDto,
  stations: AssignmentDto[] | undefined | null
): string | undefined {
  if (!stations || stations.length === 0) {
    return undefined;
  }

  // Order stations by their event date (ascending).
  const sorted = [...stations]
    .filter(s => s.EventDate)
    .sort((a, b) => new Date(a.EventDate!).getTime() - new Date(b.EventDate!).getTime());

  if (sorted.length === 0) {
    return undefined;
  }

  // First station that is NOT fully assigned (still open).
  const firstOpen = sorted.find(s => s.StationFullyAssigned !== true);

  // All stations fully assigned -> show the main's original event date.
  if (!firstOpen) {
    return main.EventDate ?? undefined;
  }

  return firstOpen.EventDate ?? undefined;
}

